import { Alert, Button, Input, Modal } from "antd";
import { MailOutlined, MobileOutlined } from "@ant-design/icons";
import { useEffect, useRef, useState } from "react";
import OTPInput from "react-otp-input";
import { useMutation } from "react-query";
import { useDispatch } from "react-redux";

import { UserApi } from "../../services";
import { actions as AuthActions } from "../../store/authRedux/actions";
import { helpers } from "../../utils";
import ModalTitle from "../ModalTitle";
import styles from "./styles.module.css";

/* همان ۶۰ ثانیه‌ی صفحه‌ی ورود. سقف واقعی روی سرور است (۶ درخواست در دقیقه)؛
 * این فقط جلوی کوبیدن روی دکمه را می‌گیرد. */
const RESEND_SECONDS = 60;

const CHANNELS = {
  email: {
    label: "ایمیل",
    icon: <MailOutlined />,
    placeholder: "example@mail.com",
    inputMode: "email",
    /* پوشه‌ی اسپم را باید گفت: نامه‌ی اولِ یک دامنه تقریباً همیشه آنجا می‌نشیند
       و کاربر تا وقتی نداند، فکر می‌کند چیزی نیامده. */
    sent: (value) =>
      `کد تأیید به ${value} فرستاده شد. اگر در صندوق ورودی نبود، پوشه اسپم رو هم نگاه کن.`,
  },
  mobile: {
    label: "شماره همراه",
    icon: <MobileOutlined />,
    placeholder: "۰۹xxxxxxxxx",
    inputMode: "numeric",
    sent: (value) => `کد تأیید به ${value} پیامک شد.`,
  },
};

const formatCountdown = (seconds) => {
  const minutes = Math.floor(seconds / 60);
  const rest = String(seconds % 60).padStart(2, "0");
  return helpers.toFa(`${minutes}:${rest}`);
};

/**
 * ثبت یا تغییر ایمیل و شماره همراهِ خودِ کاربر، در دو قدم.
 *
 * قدم اول مقدار تازه را می‌گیرد و سرور کد را به همان مقصدِ *جدید* می‌فرستد؛ قدم
 * دوم کد را برمی‌گرداند. تا کد برنگردد هیچ چیزی روی حساب نوشته نمی‌شود، چون
 * نوشتنِ نشانی‌ای که هنوز ثابت نشده در دستِ کاربر است، همان کاری است که این
 * صفحه برای جلوگیری از آن ساخته شده — و از وقتی «ورود با گوگل» هست، ایمیل دیگر
 * یک فیلد اطلاعاتی ساده نیست.
 */
const ContactChangeModal = ({ open, channel = "email", current, onClose }) => {
  const dispatch = useDispatch();
  const meta = CHANNELS[channel] || CHANNELS.email;

  const [value, setValue] = useState("");
  const [token, setToken] = useState("");
  const [sentTo, setSentTo] = useState(null);
  const [error, setError] = useState(null);
  const [resendIn, setResendIn] = useState(0);
  const valueRef = useRef(null);

  const reset = () => {
    setValue("");
    setToken("");
    setSentTo(null);
    setError(null);
    setResendIn(0);
  };

  useEffect(() => {
    if (open) reset();
  }, [open, channel]); // eslint-disable-line react-hooks/exhaustive-deps

  /* یک اینتروال برای کل شمارش، نه یک setTimeout در ثانیه که بعد از بسته شدن
     مودال هم شلیک کند. */
  useEffect(() => {
    if (resendIn <= 0) return undefined;

    const timer = window.setInterval(
      () => setResendIn((left) => (left > 0 ? left - 1 : 0)),
      1000,
    );
    return () => window.clearInterval(timer);
  }, [resendIn > 0]); // eslint-disable-line react-hooks/exhaustive-deps

  const requestCode = useMutation(UserApi.requestContactChange, {
    onSuccess: (res) => {
      setSentTo(res?.data?.value || value);
      setToken("");
      setError(null);
      setResendIn(RESEND_SECONDS);
    },
    /* پیام سرور اینجا هم داخل مودال می‌نشیند: نوتیفیکیشن گوشه‌ی صفحه چند ثانیه
       بعد می‌رود و «۲۳ ساعت دیگر» دقیقاً همان چیزی است که کاربر باید ببیند. */
    onError: (err) => setError(err?.response?.data?.message || null),
  });

  const verifyCode = useMutation(UserApi.verifyContactChange, {
    onSuccess: (res) => {
      if (res?.data?.user) dispatch(AuthActions.setUser(res.data.user));
      onClose?.(res?.data?.user || null);
    },
    onError: (err) => {
      setToken("");
      setError(err?.response?.data?.message || null);
    },
  });

  const loading = requestCode.isLoading || verifyCode.isLoading;

  const submitValue = () => {
    const clean =
      channel === "mobile"
        ? helpers.convertPerToEn(value).replace(/[^\d]/g, "")
        : value.trim();

    if (!clean) return;

    setError(null);
    requestCode.mutate({ body: { channel, value: clean } });
  };

  const submitToken = (code) => {
    setError(null);
    verifyCode.mutate({ body: { channel, token: helpers.convertPerToEn(code) } });
  };

  return (
    <Modal
      className="ds-modal"
      open={open}
      onCancel={() => !loading && onClose?.(null)}
      centered
      width={440}
      maskClosable={!loading}
      destroyOnClose
      title={
        <ModalTitle
          icon={meta.icon}
          tone="blue"
          title={`${current ? "تغییر" : "ثبت"} ${meta.label}`}
          subtitle={
            sentTo
              ? "کد فرستاده‌شده را وارد کنید"
              : `${meta.label} تازه را وارد کنید تا کد تأیید برایش فرستاده شود`
          }
        />
      }
      footer={
        <div className={styles.footer}>
          <Button disabled={loading} onClick={() => onClose?.(null)}>
            انصراف
          </Button>
          {!sentTo && (
            <Button
              type="primary"
              loading={requestCode.isLoading}
              disabled={!value.trim()}
              onClick={submitValue}
            >
              ارسال کد تأیید
            </Button>
          )}
        </div>
      }
    >
      {!sentTo ? (
        <>
          {current && (
            <p className={styles.lead}>
              {meta.label} فعلی: <strong>{current}</strong>
            </p>
          )}

          <Input
            ref={valueRef}
            size="large"
            autoFocus
            dir="ltr"
            inputMode={meta.inputMode}
            placeholder={meta.placeholder}
            value={value}
            onChange={(event) => setValue(event.target.value)}
            onPressEnter={() => !loading && submitValue()}
          />

          <p className={styles.hint}>
            {meta.label} را هر ۲۴ ساعت یک بار می‌توان تغییر داد.
          </p>
        </>
      ) : (
        <>
          <p className={styles.lead}>{meta.sent(sentTo)}</p>

          <div className={styles.otpWrap}>
            <OTPInput
              containerStyle={{
                width: "100%",
                justifyContent: "space-between",
                flexDirection: "row-reverse",
                gap: "10px",
              }}
              numInputs={5}
              value={token}
              onChange={(next) => {
                if (error) setError(null);
                const clean = helpers
                  .convertPerToEn(next)
                  .replace(/[^\d]/g, "");
                setToken(clean);
                if (clean.length === 5 && !loading) submitToken(clean);
              }}
              renderInput={(props, index) => (
                <input
                  key={`contact-otp-${index}`}
                  {...props}
                  className={`${styles.otpInput} ${
                    error ? styles.otpInputError : ""
                  }`}
                  inputMode="numeric"
                  autoComplete="one-time-code"
                />
              )}
              shouldAutoFocus
              inputType="text"
            />
          </div>

          <div className={styles.resendRow}>
            {resendIn > 0 ? (
              <span className={styles.resendWait}>
                ارسال مجدد کد تا {formatCountdown(resendIn)} دیگر
              </span>
            ) : (
              <Button
                type="link"
                size="small"
                className={styles.resendButton}
                loading={requestCode.isLoading}
                onClick={submitValue}
              >
                ارسال مجدد کد
              </Button>
            )}
          </div>
        </>
      )}

      {error && (
        <Alert className={styles.error} type="error" showIcon message={error} />
      )}
    </Modal>
  );
};

export default ContactChangeModal;
